import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Lightbulb, Send, CheckCircle2, Sparkles, Clock, Rocket } from 'lucide-react';

/**
 * Tell-us-what-your-division-needs form.
 * Rendered on the Quick-Start Guide page so every leader sees it.
 */
export default function DivisionWishlistForm() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    title: '',
    problem: '',
    solution: '',
    example_reference: '',
    priority: 'should_have',
    people_impacted: 0,
    expected_benefit: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [mine, setMine] = useState([]);
  const [loadingMine, setLoadingMine] = useState(true);

  const loadMine = async () => {
    setLoadingMine(true);
    try {
      const r = await api.get('/wishlist/mine');
      setMine(r.data);
    } catch { /* silent */ } finally {
      setLoadingMine(false);
    }
  };
  useEffect(() => { loadMine(); }, []);

  const submit = async () => {
    if (!form.title.trim()) { setError('Please give your request a short title'); return; }
    setSubmitting(true); setError('');
    try {
      await api.post('/wishlist', {
        ...form,
        people_impacted: parseInt(form.people_impacted) || 0,
      });
      setSubmitted(true);
      setForm({ title: '', problem: '', solution: '', example_reference: '', priority: 'should_have', people_impacted: 0, expected_benefit: '' });
      await loadMine();
      setTimeout(() => setSubmitted(false), 3500);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Submit failed');
    } finally { setSubmitting(false); }
  };

  const STATUS_META = {
    submitted:     { label: 'Submitted',     color: '#6B7280', bg: '#F3F4F6', icon: Clock },
    evaluating:    { label: 'Evaluating',    color: '#BA7517', bg: '#FEF3C7', icon: Sparkles },
    approved:      { label: 'Approved',      color: '#2D8B5E', bg: '#DCFCE7', icon: CheckCircle2 },
    in_development:{ label: 'In Development',color: '#3B5BA9', bg: '#EEF2FF', icon: Rocket },
    shipped:       { label: 'Shipped',       color: '#2D8B5E', bg: '#DCFCE7', icon: CheckCircle2 },
    declined:      { label: 'Declined',      color: '#6B7280', bg: '#F3F4F6', icon: Clock },
  };

  return (
    <section className="mb-7">
      <h2 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#9CA3AF' }}>
        Tell us what your division needs
      </h2>
      <div className="bg-white rounded-xl border p-5" style={{ borderColor: '#E5E1DC' }} data-testid="wishlist-form">
        <div className="flex items-start gap-3 mb-3">
          <div className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FEF3C715' }}>
            <Lightbulb size={18} style={{ color: '#BA7517' }} />
          </div>
          <div>
            <p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Custom request for your division</p>
            <p className="text-[12px] mt-0.5" style={{ color: '#6B7280' }}>
              For division-specific needs that aren't in ScaleBoard yet — like when Aditya asked for Shopify sync and we built it for TDB.
              Describe the problem in detail; the more context, the faster we can evaluate + ship.
            </p>
          </div>
        </div>

        {submitted && (
          <div className="rounded-md p-2.5 mb-3 flex items-center gap-2 text-[12px] font-medium" style={{ backgroundColor: '#DCFCE7', color: '#2D8B5E' }} data-testid="wishlist-submitted">
            <CheckCircle2 size={13} /> Thank you! Your request has been logged. Dipali will review and respond.
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#9CA3AF' }}>Title *</label>
            <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="e.g., Shopify sync for Doggy Bakery products"
              className="mt-1 w-full text-sm border rounded px-3 py-2" style={{ borderColor: '#E5E1DC' }} maxLength={200}
              data-testid="wishlist-title" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#9CA3AF' }}>The problem you face today</label>
              <textarea value={form.problem} onChange={e => setForm(f => ({ ...f, problem: e.target.value }))}
                rows={4} maxLength={4000}
                placeholder="What are you struggling with? What's the current workaround (if any)?"
                className="mt-1 w-full text-[13px] border rounded px-3 py-2 resize-none" style={{ borderColor: '#E5E1DC' }}
                data-testid="wishlist-problem" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#9CA3AF' }}>The solution you'd love to see</label>
              <textarea value={form.solution} onChange={e => setForm(f => ({ ...f, solution: e.target.value }))}
                rows={4} maxLength={4000}
                placeholder="What should ScaleBoard do differently? Draw it with words — fields, screens, buttons, actions."
                className="mt-1 w-full text-[13px] border rounded px-3 py-2 resize-none" style={{ borderColor: '#E5E1DC' }}
                data-testid="wishlist-solution" />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#9CA3AF' }}>Example / reference (optional)</label>
            <input type="text" value={form.example_reference} onChange={e => setForm(f => ({ ...f, example_reference: e.target.value }))}
              placeholder="e.g., 'Like the Shopify sync Aditya uses for TDB' or a link/tool you've seen elsewhere"
              className="mt-1 w-full text-[13px] border rounded px-3 py-2" style={{ borderColor: '#E5E1DC' }} maxLength={1000}
              data-testid="wishlist-reference" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#9CA3AF' }}>Priority</label>
              <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                className="mt-1 w-full text-[13px] border rounded px-2 py-2" style={{ borderColor: '#E5E1DC' }}
                data-testid="wishlist-priority">
                <option value="must_have">🔥 Must-have — blocking me</option>
                <option value="should_have">⭐ Should-have — high-value</option>
                <option value="nice_to_have">💡 Nice-to-have — when you can</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#9CA3AF' }}>People impacted</label>
              <input type="number" min="0" value={form.people_impacted} onChange={e => setForm(f => ({ ...f, people_impacted: e.target.value }))}
                placeholder="How many people in your division benefit?"
                className="mt-1 w-full text-[13px] border rounded px-2 py-2" style={{ borderColor: '#E5E1DC' }}
                data-testid="wishlist-people" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#9CA3AF' }}>Expected benefit</label>
              <input type="text" value={form.expected_benefit} onChange={e => setForm(f => ({ ...f, expected_benefit: e.target.value }))}
                placeholder="e.g., '2 hrs/week saved per outlet'"
                className="mt-1 w-full text-[13px] border rounded px-2 py-2" style={{ borderColor: '#E5E1DC' }} maxLength={500}
                data-testid="wishlist-benefit" />
            </div>
          </div>

          {error && (
            <div className="rounded-md px-2.5 py-2 text-[11px]" style={{ backgroundColor: '#FEF2F2', color: '#D4726A' }} data-testid="wishlist-error">
              {error}
            </div>
          )}

          <div className="flex items-center justify-between">
            <p className="text-[11px]" style={{ color: '#9CA3AF' }}>
              You'll see this in your "My requests" list below and track its progress.
            </p>
            <button onClick={submit} disabled={submitting || !form.title.trim()}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded text-white disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #BA7517 0%, #8C560F 100%)' }}
              data-testid="wishlist-submit">
              <Send size={13} /> {submitting ? 'Submitting…' : 'Submit request'}
            </button>
          </div>
        </div>
      </div>

      {/* My requests */}
      {mine.length > 0 && (
        <div className="mt-4 bg-white rounded-xl border p-5" style={{ borderColor: '#E5E1DC' }} data-testid="wishlist-mine">
          <p className="text-sm font-bold mb-3" style={{ color: '#1B2A4A' }}>My requests <span className="font-normal text-[11px]" style={{ color: '#6B7280' }}>({mine.length})</span></p>
          <div className="divide-y" style={{ borderColor: '#F3F4F6' }}>
            {mine.map(r => {
              const sm = STATUS_META[r.status] || STATUS_META.submitted;
              const Icon = sm.icon;
              return (
                <div key={r.id} className="py-2.5 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold" style={{ color: '#1B2A4A' }}>{r.title}</p>
                    {r.admin_note && (
                      <p className="text-[11px] mt-0.5 italic" style={{ color: '#6B7280' }}>Dipali's note: "{r.admin_note}"</p>
                    )}
                  </div>
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0"
                    style={{ backgroundColor: sm.bg, color: sm.color }}>
                    <Icon size={10} /> {sm.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
